import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import aiDevelopmentService from "../../services/aiDevelopmentService";

const ABSOLUTE_MIN_ZOOM = 0.1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.5;

// The image is sized by explicit pixel width/height (naturalSize * zoom), not CSS max-width/
// max-height + transform:scale(). That makes zoom=1 an unambiguous "actual size" (1 image px =
// 1 screen px), which in turn makes "fit to screen" a real, independently computed value (often
// < 1 for large images) instead of just being an alias for the same zoom=1 the reset button uses.
const computeFitZoom = (naturalWidth, naturalHeight, viewportWidth, viewportHeight) => {
  if (!naturalWidth || !naturalHeight || !viewportWidth || !viewportHeight) return 1;
  const fit = Math.min(viewportWidth / naturalWidth, viewportHeight / naturalHeight);
  return Math.min(1, Math.max(ABSOLUTE_MIN_ZOOM, fit));
};

export default function ImageLightbox({
  attachmentIds,
  index,
  attachmentById,
  onClose,
  onNavigate,
}) {
  const { t } = useTranslation();
  const [url, setUrl] = useState("");
  const [zoom, setZoom] = useState(1);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [error, setError] = useState(false);
  const panRef = useRef({ dragging: false, x: 0, y: 0, left: 0, top: 0 });
  const scrollRef = useRef(null);
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const lastFitZoomRef = useRef(1);
  const attachmentId = attachmentIds[index];
  const attachment = attachmentById[attachmentId];

  useEffect(() => {
    let current = "";
    let cancelled = false;
    setUrl("");
    setError(false);
    setZoom(1);
    setNaturalSize({ width: 0, height: 0 });
    aiDevelopmentService
      .attachmentBlob(attachmentId)
      .then((blob) => {
        if (cancelled) return;
        current = URL.createObjectURL(blob);
        setUrl(current);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      if (current) URL.revokeObjectURL(current);
    };
  }, [attachmentId]);

  // Move focus into the dialog on open and restore it to whatever triggered the lightbox
  // (e.g. the inline image) on close, instead of silently dropping keyboard focus to <body>.
  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    dialogRef.current?.focus();
    return () => {
      const previous = previouslyFocusedRef.current;
      if (previous && typeof previous.focus === "function" && document.contains(previous)) {
        previous.focus();
      }
    };
  }, []);

  const fitToScreen = useCallback(() => {
    const container = scrollRef.current;
    if (!container || !naturalSize.width || !naturalSize.height) {
      setZoom(1);
      lastFitZoomRef.current = 1;
      return;
    }
    const rect = container.getBoundingClientRect();
    const fit = computeFitZoom(naturalSize.width, naturalSize.height, rect.width, rect.height);
    lastFitZoomRef.current = fit;
    setZoom(fit);
  }, [naturalSize]);

  const onImageLoad = (e) => {
    const img = e.currentTarget;
    const size = { width: img.naturalWidth, height: img.naturalHeight };
    setNaturalSize(size);
    const container = scrollRef.current;
    const rect = container ? container.getBoundingClientRect() : null;
    const fit = computeFitZoom(size.width, size.height, rect?.width, rect?.height);
    lastFitZoomRef.current = fit;
    setZoom(fit);
  };

  // Keep the image fitted across window resizes, but only while the user hasn't zoomed away
  // from the last computed fit value - an explicit manual zoom should not be silently undone.
  useEffect(() => {
    const onResize = () => {
      if (!naturalSize.width || !naturalSize.height || !scrollRef.current) return;
      setZoom((current) => {
        if (Math.abs(current - lastFitZoomRef.current) > 0.001) return current;
        const rect = scrollRef.current.getBoundingClientRect();
        const fit = computeFitZoom(naturalSize.width, naturalSize.height, rect.width, rect.height);
        lastFitZoomRef.current = fit;
        return fit;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [naturalSize]);

  // Whether the scaled image actually overflows its viewport - this drives pan availability and
  // the grab cursor, since at a "fit" zoom (which can be well below 1) the image never overflows
  // and dragging would have nothing to do. Computed analytically from zoom vs. the last fit zoom
  // for the current container size (both already tracked above) rather than by measuring
  // scrollWidth/clientWidth after the CSS width/height transition settles: the DOM-measurement
  // approach depends on a transitionend/rAF firing promptly, which browsers can throttle
  // arbitrarily for a backgrounded/non-visible tab, leaving pan silently stuck off.
  const overflowing = zoom > lastFitZoomRef.current + 0.001;

  const close = useCallback(() => onClose(), [onClose]);
  const next = useCallback(() => {
    if (index < attachmentIds.length - 1) onNavigate(index + 1);
  }, [index, attachmentIds.length, onNavigate]);
  const prev = useCallback(() => {
    if (index > 0) onNavigate(index - 1);
  }, [index, onNavigate]);

  const zoomOut = useCallback(
    () => setZoom((z) => Math.max(ABSOLUTE_MIN_ZOOM, z - ZOOM_STEP)),
    [],
  );
  const zoomIn = useCallback(() => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP)), []);
  const resetZoom = useCallback(() => setZoom(1), []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
      else if (e.key === "0") resetZoom();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [close, next, prev, zoomIn, zoomOut, resetZoom]);

  const startPan = (e) => {
    if (!overflowing || !scrollRef.current) return;
    panRef.current = {
      dragging: true,
      x: e.clientX,
      y: e.clientY,
      left: scrollRef.current.scrollLeft,
      top: scrollRef.current.scrollTop,
    };
  };
  const doPan = (e) => {
    if (!panRef.current.dragging || !scrollRef.current) return;
    scrollRef.current.scrollLeft = panRef.current.left - (e.clientX - panRef.current.x);
    scrollRef.current.scrollTop = panRef.current.top - (e.clientY - panRef.current.y);
  };
  const endPan = () => {
    panRef.current.dragging = false;
  };

  const imageStyle =
    naturalSize.width && naturalSize.height
      ? {
          width: naturalSize.width * zoom,
          height: naturalSize.height * zoom,
          // .ai-lightbox-body is a flex container, and a flex item's default flex-shrink:1 lets
          // the browser silently shrink it below an explicit width/height to avoid overflowing
          // the container - which would defeat zoom entirely above the "fit" level (100%/zoom-in
          // would always get shrunk right back down, and overflow/pan would never engage since
          // the item never actually exceeds the container). Pin it to its explicit size instead.
          flexShrink: 0,
          maxWidth: "none",
          maxHeight: "none",
        }
      : undefined;

  return createPortal(
    <div
      className="ai-lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={attachment?.fileName || t("aiDevelopment.image")}
      tabIndex={-1}
      ref={dialogRef}
      onClick={close}
    >
      <div className="ai-lightbox-toolbar" onClick={(e) => e.stopPropagation()}>
        <span className="ai-lightbox-filename">{attachment?.fileName}</span>
        <div className="ai-lightbox-controls">
          <button type="button" aria-label={t("aiDevelopment.zoomOut")} onClick={zoomOut}>
            −
          </button>
          <button
            type="button"
            aria-label={t("aiDevelopment.resetZoom")}
            title={t("aiDevelopment.resetZoom")}
            onClick={resetZoom}
          >
            {Math.round(zoom * 100)}%
          </button>
          <button type="button" aria-label={t("aiDevelopment.zoomIn")} onClick={zoomIn}>
            +
          </button>
          <button
            type="button"
            aria-label={t("aiDevelopment.fitToScreen")}
            title={t("aiDevelopment.fitToScreen")}
            onClick={fitToScreen}
          >
            {t("aiDevelopment.fitToScreen")}
          </button>
          <button type="button" aria-label={t("common.close")} onClick={close}>
            ✕
          </button>
        </div>
      </div>
      <div
        className="ai-lightbox-body"
        ref={scrollRef}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={startPan}
        onMouseMove={doPan}
        onMouseUp={endPan}
        onMouseLeave={endPan}
        style={{ cursor: overflowing ? "grab" : "default" }}
      >
        {error ? (
          <p className="ai-lightbox-error">{t("aiDevelopment.imageLoadError")}</p>
        ) : url ? (
          <img
            src={url}
            alt={attachment?.fileName || ""}
            style={imageStyle}
            onLoad={onImageLoad}
            draggable={false}
          />
        ) : (
          <p>{t("common.loading")}</p>
        )}
      </div>
      {attachmentIds.length > 1 && (
        <>
          <button
            type="button"
            className="ai-lightbox-nav ai-lightbox-prev"
            aria-label={t("aiDevelopment.previousImage")}
            disabled={index === 0}
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
          >
            ‹
          </button>
          <button
            type="button"
            className="ai-lightbox-nav ai-lightbox-next"
            aria-label={t("aiDevelopment.nextImage")}
            disabled={index === attachmentIds.length - 1}
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
          >
            ›
          </button>
        </>
      )}
    </div>,
    document.body
  );
}
